import {
  hivemindConstants,
  generateDefaultAllocations,
  getSubjectKey,
  groupEligibleExperimentsById,
  MockWrapper,
  updateCohortAllocationsWithEid,
  validateApp
} from '@godaddy/hivemind-common'

import loadStorage from './load-storage'
import SplitioWrapper from './splitio-wrapper'

const TIMEOUT_DURATION = 3000

export default class HivemindClient {
  constructor ({
    app,
    splitioWrappers,
    splitioOptions,
    subject,
    loadLocalStorage = loadStorage,
    readyTimeout = TIMEOUT_DURATION,
    mockData,
    preloadLocation,
    usePreload = true
  }) {
    validateApp(app)
    this._app = app
    this._readyTimeout = readyTimeout

    let preloadExists = false
    usePreload && Object.entries(subject).forEach(([idType, id]) => {
      preloadExists = loadLocalStorage({
        app: this._app,
        idType,
        id,
        preloadLocation
      })
    })
    this._subject = subject

    if (mockData) {
      splitioWrappers = this._getMockWrappers(mockData, subject)
    }

    this._splitioWrappers = splitioWrappers || Object.entries(subject).reduce((wrappers, [idType, id]) => {
      wrappers[idType] = new SplitioWrapper({
        app: this._app,
        idType,
        id,
        splitioOptions,
        preloadExists
      })
      return wrappers
    }, {})

    // Some operations are subject-agnostic. For these, we can use any wrapper.
    this._defaultSplitioWrapper = Object.values(this._splitioWrappers)[0]

    // The hivemind sdk is ready when all of the Split.io sdks are ready
    // In the event that the sdk is not ready by readyTimeout the promise will still resolve
    // but some of the methods may return default 'ineligible' values
    this._ready = new Promise(resolve =>
      Promise.race([
        Promise.all(Object.values(this._splitioWrappers).map(wrapper => wrapper.ready)),
        new Promise((resolve, reject) => setTimeout(() => reject(new Error('timeout')), this._readyTimeout))
      ])
        .then(resolve)
        .catch(err => {
          console.log('Hivemind Client not ready from cache. Will return default values.', err)
          resolve('timeout')
        })
    )
  }

  get ready () {
    return this._ready
  }

  setMockData (mockData) {
    this._splitioWrappers = this._getMockWrappers(mockData, this._subject)
    this._defaultSplitioWrapper = Object.values(this._splitioWrappers)[0]
  }

  _getMockWrappers (mockData, subject) {
    return Object.entries(subject).reduce((wrappers, [idType, id]) => {
      wrappers[idType] = new MockWrapper({ mockData })
      return wrappers
    }, {})
  }

  getCohorts ({ experiments = [], labels = [], attributes = {}, windowOverride = window }) {
    if (experiments.length) {
      const experimentsMetadata = this._defaultSplitioWrapper.getExperimentsById(experiments)
      return this._getCohorts({ experimentsMetadata, experiments, attributes, windowOverride })
    } else if (labels.length) {
      const experimentsMetadata = this._getExperimentsByLabel(labels)
      const experimentIds = Object.keys(experimentsMetadata)
      return this._getCohorts({ experimentsMetadata, experiments: experimentIds, attributes, windowOverride })
    }
    console.log('No experiments or labels provided')
    return this._getCohorts({ attributes, windowOverride })
  }

  _getCohorts ({ experimentsMetadata = {}, experiments = [], attributes = {}, windowOverride = window }) {
    const { experimentsByIdType, ineligibleExperiments } = groupEligibleExperimentsById({
      subject: this._subject,
      experiments,
      experimentsMetadata
    })

    let cohortAllocations = {}
    Object.entries(experimentsByIdType).map(([idType, idTypeExperiments]) => {
      const partialCohortAllocations = this._splitioWrappers[idType]
        .getCohortAllocations({ experiments: idTypeExperiments, attributes })
      cohortAllocations = { ...cohortAllocations, ...partialCohortAllocations }
    })

    cohortAllocations = updateCohortAllocationsWithEid(cohortAllocations, this._app)

    const subjectKeys = getSubjectKeys(this._subject)

    logCohorts(cohortAllocations, subjectKeys, windowOverride)

    Object.keys(cohortAllocations).forEach(key => delete cohortAllocations[key].error)

    const ineligibleAllocations = generateDefaultAllocations(ineligibleExperiments)
    cohortAllocations = { ...cohortAllocations, ...ineligibleAllocations }

    return { cohorts: cohortAllocations }
  }

  getExperiments ({ labels = [] } = {}) {
    const experiments = labels.length ? this._getExperimentsByLabel(labels) : this._defaultSplitioWrapper.getExperiments()
    return Object.values(experiments)
  }

  _getExperimentsByLabel (labels) {
    const allExperiments = this._defaultSplitioWrapper.getExperiments()
    return Object.entries(allExperiments).reduce((mapping, [id, metadata]) => {
      if (labels.every(label => metadata.labels.includes(label))) mapping[id] = metadata
      return mapping
    }, {})
  }

  getParameterValue ({ parameter, defaultValue, attributes = {}, windowOverride = window }) {
    const parameterExperiments = this._getExperimentsByLabel([this._app, hivemindConstants.PARAMETER_LABEL])
    const matchingExperimentsMetadata = Object.entries(parameterExperiments).reduce((mapping, [id, metadata]) => {
      if (Object.values(metadata.cohortData).some(cohortMetadata => parameter in cohortMetadata)) {
        mapping[id] = metadata
      }
      return mapping
    }, {})
    const matchingExperiments = Object.keys(matchingExperimentsMetadata)
    const numMatching = matchingExperiments.length
    if (numMatching === 1) {
      const { cohorts } = this._getCohorts({
        experimentsMetadata: matchingExperimentsMetadata,
        experiments: matchingExperiments,
        attributes,
        windowOverride
      })
      const cohort = cohorts[matchingExperiments[0]]
      const cohortData = cohort.data
      return {
        parameterValue: parameter in cohortData ? cohortData[parameter] : defaultValue,
        cohortId: cohort.cohortId,
        experimentId: matchingExperiments[0]
      }
    } else if (numMatching > 1) {
      console.log('Multiple experiments containing the same parameter definition.')
    }
    return { parameterValue: defaultValue, cohortId: null, experimentId: null }
  }
}

export function getSubjectKeys (subject) {
  const subjectKeys = {}
  Object.entries(subject).forEach(([idType, id]) => {
    subjectKeys[idType] = getSubjectKey({ idType, id })
  })
  return subjectKeys
}

export function logCohorts (cohorts, subjectKeys, windowOverride = window) {
  windowOverride._expDataLayer = windowOverride._expDataLayer || []
  for (const experimentId in cohorts) {
    windowOverride._expDataLayer.push({
      schema: 'add_experiment_assignment',
      type: 'abn',
      version: 'v1',
      data: {
        experiment_id: experimentId,
        variant_id: cohorts[experimentId].error ? 'error' : cohorts[experimentId].cohortId
      }
    })
    windowOverride._expDataLayer.push({
      schema: 'add_impression',
      version: 'v1',
      data: {
        eid: cohorts[experimentId].eid,
        properties: {
          hashedShopperId: subjectKeys[hivemindConstants.idTypes.SHOPPER_ID],
          visitorGuid: subjectKeys[hivemindConstants.idTypes.VISITOR_GUID]
        }
      }
    })
  }
}
